import { Configuration, PopupRequest, RedirectRequest } from '@azure/msal-browser';

// Helper function to detect if we're in incognito/private mode
const isIncognitoMode = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Try to detect incognito mode using various methods
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        // In incognito mode, quota is usually very small
        resolve((estimate.quota || 0) < 120000000); // Less than ~120MB suggests incognito
      }).catch(() => resolve(false));
    } else {
      // Fallback: try localStorage test
      try {
        localStorage.setItem('__incognito_test__', 'test');
        localStorage.removeItem('__incognito_test__');
        resolve(false); // localStorage works, probably not incognito
      } catch {
        resolve(true); // localStorage blocked, likely incognito
      }
    }
  });
};

// Validate environment variables
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;

// Azure AD configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId || '',
    authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for better persistence
    storeAuthStateInCookie: false, // Keep false for modern browsers
    claimsBasedCachingEnabled: false, // Disable to prevent caching issues
  },
  system: {
    allowNativeBroker: false, // Disables WAM Broker
    iframeHashTimeout: 6000, // Balanced timeout - 6s
    loadFrameTimeout: 6000, // Balanced timeout - 6s
    navigateFrameWait: 500, // Slightly longer wait time
    windowHashTimeout: 6000, // Balanced timeout
    tokenRenewalOffsetSeconds: 120, // Add token renewal offset
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error('MSAL Error:', message);
            return;
          case 1: // LogLevel.Warning
            console.warn('MSAL Warning:', message);
            return;
          case 2: // LogLevel.Info
            // Only log important info
            if (message.includes('handleRedirectPromise') || message.includes('Interaction')) {
              console.info('MSAL Info:', message);
            }
            return;
          case 3: // LogLevel.Verbose
            // Disable verbose logs
            return;
        }
      }
    }
  },
};

// Add scopes here for ID token to be used at Microsoft identity platform endpoints.
export const loginRequest: RedirectRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
  prompt: 'select_account', // Always show account picker
  loginHint: undefined, // Don't pre-fill any account
  redirectUri: window.location.origin,
};

// Silent request configuration for token refresh
export const silentRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
  forceRefresh: false, // Set to "true" to skip a cached token and go to the server to get a new token
};

// Add the endpoints here for Microsoft Graph API services you'd like to use.
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};

// Export the incognito detection function
export { isIncognitoMode }; 